import { useState } from 'react'
import { AxiosError } from 'axios'
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Skeleton,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { useRecomputeStatus, useTriggerRecompute } from '../../hooks/useScoringOptions'

interface RecomputePanelProps {
  /** True once options have been saved but no recompute has been run since. */
  recomputePending?: boolean
}

function formatTime(value: string | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleString()
}

/**
 * Status and manual trigger for the leaderboard recompute.
 *
 * The job runs in the analytics worker, not in the API, so triggering is a request over Redis rather
 * than an in-process call — hence "requested" language and the possibility of no worker listening.
 */
export function RecomputePanel({ recomputePending }: RecomputePanelProps) {
  const { data: status, isLoading, error: statusError, refetch } = useRecomputeStatus()
  const trigger = useTriggerRecompute()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const isRunning = status?.isRunning ?? false

  // The 500 path in the controller returns `Message` (capital M), the 409/503 paths `message`.
  const triggerError = trigger.error as AxiosError<{ message?: string; Message?: string }> | null
  const triggerStatus = triggerError?.response?.status
  const triggerErrorMessage = triggerError
    ? triggerStatus === 409
      ? 'A recompute is already running.'
      : triggerStatus === 503
        ? 'No analytics worker is listening, so the recompute was not started. Check that the worker is running.'
        : !triggerError.response
          ? 'No answer from the server, so it is not known whether the recompute started. Check the status below before trying again.'
          : (triggerError.response.data?.message ??
            triggerError.response.data?.Message ??
            'Failed to request a recompute.')
    : null

  const handleConfirm = () => {
    setConfirmOpen(false)
    trigger.mutate()
  }

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Typography variant="h6" gutterBottom>
        Recompute
      </Typography>

      {recomputePending && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Scoring options were saved, but the ladders still reflect the previous settings. Run a
          recompute to apply them.
        </Alert>
      )}

      {triggerErrorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => trigger.reset()}>
          {triggerErrorMessage}
        </Alert>
      )}

      {status?.error && !isRunning && (
        <Alert severity="error" sx={{ mb: 2 }}>
          The last run failed: {status.error}
        </Alert>
      )}

      {status && !status.hasEverRun && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No recompute has ever run, so every ladder is currently empty.
        </Alert>
      )}

      {statusError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Could not read the recompute status.
        </Alert>
      )}

      {trigger.isSuccess && !isRunning && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => trigger.reset()}>
          Recompute requested. The worker picks it up within a few seconds.
        </Alert>
      )}

      {/* Until the first status answer arrives, "Idle / Started: —" would look exactly like a
          healthy idle worker, so show placeholders instead. */}
      {isLoading ? (
        <Box sx={{ mb: 2 }} aria-busy="true" aria-label="Loading recompute status">
          <Skeleton variant="rounded" width={64} height={24} sx={{ mb: 1.5 }} />
          <Skeleton width="60%" />
          <Skeleton width="60%" />
        </Box>
      ) : (
      <>
      <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
        <Chip
          label={isRunning ? 'Running' : 'Idle'}
          color={isRunning ? 'info' : 'default'}
          size="small"
        />
        {status?.source && (
          <Typography variant="body2" color="text.secondary">
            Last run: {status.source}
          </Typography>
        )}
      </Stack>

      {isRunning && <LinearProgress sx={{ mb: 2 }} />}

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Started: {formatTime(status?.startedAt ?? null)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Completed: {formatTime(status?.completedAt ?? null)}
        </Typography>
        {status?.rowsWritten != null && (
          <Typography variant="body2" color="text.secondary">
            Standings written: {status.rowsWritten.toLocaleString()}
          </Typography>
        )}
      </Box>
      </>
      )}

      <Button
        variant="contained"
        onClick={() => setConfirmOpen(true)}
        disabled={isLoading || isRunning || trigger.isPending}
        sx={{ minHeight: 40 }}
      >
        {trigger.isPending ? 'Requesting…' : 'Recompute now'}
      </Button>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
        A full rebuild reads every completed session, so it is deliberately manual. The worker also runs
        it automatically at 02:00 UTC daily. After it finishes, the public site can lag up to 3 minutes.
      </Typography>

      <ConfirmDialog
        open={confirmOpen}
        title="Recompute every leaderboard?"
        message="This asks the analytics worker to rebuild all score standings from every completed session, using the scoring knobs as saved now. It runs as a background job: you can leave this page, and the status here updates when it finishes."
        confirmLabel="Start recompute"
        onConfirm={handleConfirm}
        onClose={() => setConfirmOpen(false)}
      />
    </Paper>
  )
}
